import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import _ from "lodash";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { AppContext } from "../context/appContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faChevronLeft, faDatabase } from "@fortawesome/free-solid-svg-icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const AddEditMasterData = ({ type }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = location?.state?.returnTo || -1;
  const { modelname, itemId } = useParams();
  const { matchseekerMasterData, reloadContext, setReloadContext } = useContext(AppContext);

  const [schema, setSchema] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [validationSchema, setValidationSchema] = useState(Yup.object());

  const formatLabel = (field) =>
    field.charAt(0).toUpperCase() + field.slice(1);

  const buildDefaults = (schema) => {
    const result = {};
    Object.entries(schema).forEach(([key, config]) => {
      if (config.type === "Object" || config.type === "Mixed") {
        result[key] = buildDefaults(config.properties || {});
      } else if (config.type === "Array") {
        result[key] = [];
      } else if (config.type === "Boolean") {
        result[key] = config.default ?? false;
      } else if (config.type === "Number") {
        result[key] = config.default ?? 0;
      } else {
        result[key] = config.default ?? "";
      }
    });
    return result;
  };

  const buildYup = (schema) => {
    const shape = {};
    Object.entries(schema).forEach(([key, config]) => {
      if (config.type === "Object" || config.type === "Mixed") {
        shape[key] = Yup.object().shape(buildYup(config.properties || {}));
      } else if (config.type === "Array") {
        shape[key] = Yup.array()
          .of(Yup.number().typeError("Must be a number"))
          .required(`${key} is required`);
      } else if (config.type === "Number") {
        let v = Yup.number().typeError("Must be a number");
        if (config.required) v = v.required(config.required[1]);
        shape[key] = v;
      } else if (config.type === "Boolean") {
        shape[key] = Yup.boolean();
      } else {
        let v = Yup.string().trim();
        if (config.required) v = v.required(config.required[1]);
        shape[key] = v;
      }
    });
    return shape;
  };

  useEffect(() => {
    const load = async () => {
      const scRes = await func.get(`mst-admin/${modelname}${urls.GET_MODAL_SCHEMA_URL}`);
      const sc = scRes.data;
      setSchema(sc);

      let defaults = buildDefaults(sc);

      if (type === "edit" && itemId) {
        const docRes = await func.get(
          `mst-admin/${modelname}${urls.GET_MODAL_DETAILS_BY_ID_URL}${itemId}`
        );
        defaults = _.merge(defaults, docRes.data);
      }

      _.set(defaults, "coordinates.type", _.get(defaults, "coordinates.type") || "Point");
      _.set(defaults, "coordinates.coordinates", _.get(defaults, "coordinates.coordinates") || [0, 0]);

      setInitialValues(defaults);
      setValidationSchema(Yup.object().shape(buildYup(sc)));
    };

    load();
  }, [type, itemId, modelname]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const cleaned = _.cloneDeep(values);
      const url =
        type === "edit"
          ? `mst-admin/${modelname}${urls.EDIT_MASTER_DATA_URL}${itemId}`
          : `mst-admin/${modelname}${urls.CREATE_MASTER_DATA_URL}`;
      const res =
        type === "edit"
          ? await func.put(url, cleaned)
          : await func.post(url, cleaned);
      if (res.status === (type === "edit" ? 200 : 201)) {
        toast.success(`Record ${type === "edit" ? "updated" : "added"} successfully!`);
        setReloadContext(!reloadContext);
        navigate(returnPath, { state: { isEdited: true } });
      } else {
        toast.error("Something went wrong");
      }
    },
  });

  const getLabelForOption = (opt) =>
    opt?.name || opt?.city || opt?.state || opt?.country || opt?.profession ||
    opt?.qualification || opt?.religion || opt?.caste || opt?.language ||
    opt?.zodiacSign || opt?.dietType || opt?.familyType || opt?.gotram ||
    opt?.heights || opt?.belief || opt?.subcaste || opt?.gender ||
    opt?.hicategory || "Unknown";

  const getOptionsForField = (field) => {
    const refKey = schema[field]?.refSchema?.replace(/_/g, "-");
    return matchseekerMasterData?.[refKey] || [];
  };

  const inputCls =
    "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";

  const renderField = (key, config, path = key) => {
    if (config.type === "Object" || config.type === "Mixed") {
      return (
        <div key={path} className="md:col-span-3 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(config.properties || {}).map(([k, c]) =>
              renderField(k, c, `${path}.${k}`)
            )}
          </div>
        </div>
      );
    }

    if (config.type === "Array" && path === "coordinates.coordinates") {
      return (
        <>
          <div key={path} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Longitude *
            </label>
            <input
              type="number"
              step="any"
              placeholder="Longitude"
              {...formik.getFieldProps(`${path}[0]`)}
              className={`${inputCls} no-scroll-input`}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Latitude *
            </label>
            <input
              type="number"
              step="any"
              placeholder="Latitude"
              {...formik.getFieldProps(`${path}[1]`)}
              className={`${inputCls} no-scroll-input`}
            />
          </div>
          {formik.touched.coordinates?.coordinates &&
            formik.errors.coordinates?.coordinates && (
              <p className="text-red-500 text-sm mt-1">
                {JSON.stringify(formik.errors.coordinates.coordinates)}
              </p>
            )}
        </>
      );
    }

    if (path === "coordinates.type") {
      return (
        <div key={path} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formatLabel("coordinates.type")} *
          </label>
          <select {...formik.getFieldProps(path)} className={inputCls}>
            <option value="">Select Value</option>
            <option value="Point">Point</option>
          </select>
          {formik.touched.coordinates?.type && formik.errors.coordinates?.type && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.coordinates.type}</p>
          )}
        </div>
      );
    }

    if (path === "description") {
      return (
        <div key={path} className="mb-4 md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <ReactQuill
            theme="snow"
            value={formik.values[path] || ""}
            onChange={(content) => formik.setFieldValue(path, content)}
            className="w-full"
          />
          {formik.errors[path] && (
            <p className="text-red-500 text-sm mt-1 capitalize">{formik.errors[path]}</p>
          )}
        </div>
      );
    }

    if (config.type === "ObjectId") {
      return (
        <div key={path} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formatLabel(key)}
          </label>
          <select {...formik.getFieldProps(path)} className={inputCls}>
            <option value="">Select Value</option>
            {getOptionsForField(key).map((opt) => (
              <option key={opt._id} value={opt._id}>
                {getLabelForOption(opt)}
              </option>
            ))}
          </select>
          {formik.errors[path] && (
            <p className="text-red-500 text-sm mt-1 capitalize">{formik.errors[path]}</p>
          )}
        </div>
      );
    }

    if (config.type === "String") {
      return (
        <div key={path} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formatLabel(key)}
          </label>
          <input type="text" {...formik.getFieldProps(path)} className={inputCls} />
          {formik.errors[path] && (
            <p className="text-red-500 text-sm mt-1 capitalize">{formik.errors[path]}</p>
          )}
        </div>
      );
    }

    if (config.type === "Number") {
      return (
        <div key={path} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formatLabel(key)}
          </label>
          <input
            type="number"
            {...formik.getFieldProps(path)}
            placeholder="Enter value"
            className={`${inputCls} no-scroll-input`}
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
            }}
          />
          {formik.errors[path] && (
            <p className="text-red-500 text-sm mt-1 capitalize">{formik.errors[path]}</p>
          )}
        </div>
      );
    }

    if (config.type === "Boolean") {
      return (
        <div key={path} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formatLabel(key)}
          </label>
          <select
            value={formik.values[path] ? "true" : "false"}
            onChange={(e) => formik.setFieldValue(path, e.target.value === "true")}
            className={inputCls}
          >
            <option value="false">FALSE</option>
            <option value="true">TRUE</option>
          </select>
          {formik.errors[path] && (
            <p className="text-red-500 text-sm mt-1 capitalize">{formik.errors[path]}</p>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
          </button>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faDatabase} className="text-white text-lg" />
            <h1 className="text-lg font-semibold text-white">
              {type === "edit" ? `Edit ${modelname}` : `Add ${modelname}`}
            </h1>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <form
            onSubmit={formik.handleSubmit}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {schema && Object.entries(schema).map(([k, c]) => renderField(k, c))}

            <div className="flex justify-center col-span-3 pt-2">
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium transition"
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                {type === "edit" ? "Update" : "Add"} Data
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddEditMasterData;
